import { useState, useEffect } from 'react';
import {
  XMarkIcon,
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  TagIcon,
  DocumentTextIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { createEvent, updateEvent, isValidUUID } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

interface EventData {
  id?: string;
  title: string;
  date: string;
  time?: string;
  location?: string;
  description?: string;
  type?: string;
  tournament_id?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  tournamentId: string;
  event?: EventData | null;
}

interface FormState {
  title: string;
  date: string;
  time: string;
  location: string;
  type: string;
  description: string;
}

const emptyForm = (): FormState => ({
  title: '',
  date: '',
  time: '',
  location: '',
  type: 'Tournament',
  description: '',
});

const EVENT_TYPES = [
  { value: 'Registration', label: 'Registration', color: 'bg-blue-50 border-blue-200 text-blue-700' },
  { value: 'Tournament', label: 'Tournament', color: 'bg-golf-orange/10 border-golf-orange/30 text-golf-orange' },
  { value: 'Ceremony', label: 'Ceremony', color: 'bg-amber-50 border-amber-200 text-amber-700' },
  { value: 'Social', label: 'Social', color: 'bg-green-50 border-green-200 text-green-700' },
];

export default function MobileEventSheet({ isOpen, onClose, tournamentId, event }: Props) {
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setError('');
      if (event) {
        setForm({
          title: event.title,
          date: event.date,
          time: event.time || '',
          location: event.location || '',
          type: event.type || 'Tournament',
          description: event.description || '',
        });
      } else {
        setForm(emptyForm());
      }
    }
  }, [isOpen, event]);

  const f = (key: keyof FormState, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.title.trim()) { setError('Event title is required'); return; }
    if (!form.date) { setError('Date is required'); return; }
    if (!form.time) { setError('Time is required'); return; }
    if (!form.location.trim()) { setError('Location is required'); return; }
    if (!isValidUUID(tournamentId)) { setError('Invalid tournament'); return; }

    try {
      setSaving(true);
      const payload = {
        tournament_id: tournamentId,
        title: form.title.trim(),
        date: form.date,
        time: form.time,
        location: form.location.trim(),
        type: form.type as 'Registration' | 'Tournament' | 'Ceremony' | 'Social',
        description: form.description.trim() || undefined,
      };

      if (event?.id) {
        const { error: err } = await updateEvent(event.id, payload);
        if (err) throw err;
        toast.success('Event updated');
      } else {
        const { error: err } = await createEvent(payload);
        if (err) throw err;
        toast.success('Event created');
      }
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const selectedType = EVENT_TYPES.find(t => t.value === form.type);

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-t-3xl shadow-2xl max-h-[94vh] flex flex-col">
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 bg-gray-200 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-2 pb-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-golf-orange/10 flex items-center justify-center">
              <CalendarDaysIcon className="w-5 h-5 text-golf-orange" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{event ? 'Edit Event' : 'Create Event'}</h2>
              <p className="text-xs text-gray-400">Fill in the schedule details</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-2xl bg-gray-100 active:bg-gray-200 transition-colors"
          >
            <XMarkIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSave} className="flex-1 overflow-y-auto">
          <div className="px-5 space-y-5 pb-4">
            {error && (
              <div className="bg-red-50 border border-red-100 rounded-2xl px-4 py-3 text-sm text-red-600 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Event Title */}
            <div className="space-y-2">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <SparklesIcon className="w-3 h-3" />
                Event Title <span className="text-red-400 normal-case tracking-normal font-normal">*</span>
              </label>
              <input
                type="text"
                value={form.title}
                onChange={e => f('title', e.target.value)}
                placeholder="e.g. Opening Ceremony"
                required
                className="w-full px-4 py-3.5 border border-gray-200 rounded-2xl text-sm font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:border-golf-orange focus:ring-2 focus:ring-golf-orange/10 bg-gray-50/50 transition-all"
              />
            </div>

            {/* Date & Time */}
            <div className="space-y-2">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <CalendarDaysIcon className="w-3 h-3" />
                Date & Time <span className="text-red-400 normal-case tracking-normal font-normal">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2.5">
                <div className="relative">
                  <input
                    type="date"
                    value={form.date}
                    onChange={e => f('date', e.target.value)}
                    required
                    className="w-full px-4 py-3.5 border border-gray-200 rounded-2xl text-sm text-gray-900 focus:outline-none focus:border-golf-orange focus:ring-2 focus:ring-golf-orange/10 bg-gray-50/50 transition-all"
                  />
                </div>
                <div className="relative">
                  <input
                    type="time"
                    value={form.time}
                    onChange={e => f('time', e.target.value)}
                    required
                    className="w-full px-4 py-3.5 border border-gray-200 rounded-2xl text-sm text-gray-900 focus:outline-none focus:border-golf-orange focus:ring-2 focus:ring-golf-orange/10 bg-gray-50/50 transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="space-y-2">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <MapPinIcon className="w-3 h-3" />
                Location <span className="text-red-400 normal-case tracking-normal font-normal">*</span>
              </label>
              <input
                type="text"
                value={form.location}
                onChange={e => f('location', e.target.value)}
                placeholder="e.g. Ikoyi Golf Club"
                required
                className="w-full px-4 py-3.5 border border-gray-200 rounded-2xl text-sm font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:border-golf-orange focus:ring-2 focus:ring-golf-orange/10 bg-gray-50/50 transition-all"
              />
            </div>

            {/* Event Type - pill selector */}
            <div className="space-y-2">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <TagIcon className="w-3 h-3" />
                Event Type <span className="text-red-400 normal-case tracking-normal font-normal">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {EVENT_TYPES.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => f('type', t.value)}
                    className={`py-2.5 px-3 rounded-xl border text-xs font-semibold transition-all ${
                      form.type === t.value
                        ? `${t.color} shadow-sm`
                        : 'bg-white border-gray-200 text-gray-500'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <DocumentTextIcon className="w-3 h-3" />
                Description
              </label>
              <textarea
                value={form.description}
                onChange={e => f('description', e.target.value)}
                rows={3}
                placeholder="Optional details about this event..."
                className="w-full px-4 py-3.5 border border-gray-200 rounded-2xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-golf-orange focus:ring-2 focus:ring-golf-orange/10 bg-gray-50/50 transition-all resize-none"
              />
            </div>

            <div className="pb-1" />
          </div>
        </form>

        {/* Actions */}
        <div className="flex gap-3 px-5 pt-4 pb-20 border-t border-gray-100 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3.5 rounded-2xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSave}
            disabled={saving || !isValidUUID(tournamentId)}
            className="flex-1 py-3.5 rounded-2xl bg-golf-orange text-white text-sm font-semibold shadow-md shadow-golf-orange/20 active:bg-golf-orange/90 disabled:opacity-60 transition-all"
          >
            {saving ? 'Saving...' : event ? 'Save Changes' : 'Create Event'}
          </button>
        </div>
      </div>
    </div>
  );
}
